import type { MapPoint, Observation } from '@/story/ledger';
import type { MentalMapSheet } from '@/ui/mentalMap';
import {
  BOARDS_AT,
  CULVERT_AT,
  CULVERT_ROUTE,
  DITCH_AT,
  HEDGE_GAP_AT,
  HEDGEROW_BOUNDS,
  HEDGEROW_NIGHT_SPAWN,
  HERD_HUDDLE_AT,
} from '@/world/hedgerow';

/**
 * Beat 11's wall map: his plan of the perimeter, with her line on it.
 *
 * `docs/beat-11-plan.md`, *the mirror is the whole beat*. It is drawn by the
 * same `MentalMap` renderer as hers, with `hand: 'his'`, and differs from hers
 * in three ways:
 * - The ground is **ruled**: the fences, the ditch and the culvert at their
 *   real positions, read off `world/hedgerow.ts`. It is not a smudged wash of
 *   where somebody walked.
 * - The margin carries **dates**.
 * - **Her route is on it, and it is the player's own**: the hedgerow wash out
 *   of the live ledger, which persists across beat boundaries in the save.
 *   Beats 7 and 10 both write it.
 *
 * **One zone, and that is load-bearing**: rooms do not share a world, so there
 * is no coordinate system for a farm-wide plan. The hedgerow is the one room
 * whose wash is the escape.
 */

/** The ledger zone her walked cells live under. `main.ts` keys the wash by room id. */
export const HIS_MAP_ZONE = 'hedgerow';

/** The band of the field he has drawn: the yard fence to just past the far hedge. */
const NORTH_Z = 11;
const SOUTH_Z = HEDGE_GAP_AT.z - 0.6;
const WEST_X = DITCH_AT.minX;
const EAST_X = HEDGEROW_BOUNDS.eastX;
/** Half the opening in a ruled line where a gap really is. */
const BOARDS_HALF = 0.9;
const HEDGE_GAP_HALF = 0.8;
/** The ruled margin, and the page's left edge a date column beyond it. */
const MARGIN_X = WEST_X - 1.5;
const PAGE_WEST = MARGIN_X - 9.5;

function line(...points: readonly (readonly [number, number])[]): readonly MapPoint[] {
  return points.map(([x, z]) => ({ x, z }));
}

function box(cx: number, cz: number, halfX: number, halfZ: number): readonly MapPoint[] {
  // Closed: the first point repeated last. That repeat is the difference
  // between his box and hers, and `mental-map-hand.spec.ts` checks it.
  return line([cx - halfX, cz - halfZ], [cx + halfX, cz - halfZ], [cx + halfX, cz + halfZ], [cx - halfX, cz + halfZ], [cx - halfX, cz - halfZ]);
}

/**
 * A hedge as a map draws one: a row of scallops along the line, each `pitch`
 * metres across, bulging north like bush tops. Ruled straight, the hedge was one more line in
 * a box of lines, and blind viewers read the whole sheet as *"a chart"* or *"a
 * schedule"* (D-240). Scallops are what every printed map uses for a hedge.
 */
function hedge(fromX: number, toX: number, z: number, pitch = 2.4): readonly MapPoint[] {
  const points: MapPoint[] = [];
  const count = Math.max(1, Math.round((toX - fromX) / pitch));
  const step = (toX - fromX) / count;
  for (let bump = 0; bump < count; bump += 1) {
    for (let k = 0; k <= 6; k += 1) {
      if (bump > 0 && k === 0) continue;
      const t = k / 6;
      // North, into the field: bulging south, the first frame's row read as waves.
      points.push({ x: fromX + (bump + t) * step, z: z + Math.sin(t * Math.PI) * step * 0.5 });
    }
  }
  return points;
}

/** Water in the ditch: short level strokes between its two edges, the map sign for a channel. */
function water(fromX: number, toX: number, nearZ: number, farZ: number): readonly (readonly MapPoint[])[] {
  const strokes: (readonly MapPoint[])[] = [];
  const mid = (nearZ + farZ) / 2;
  for (let x = fromX + 1.5; x < toX - 1; x += 3.2) strokes.push(line([x, mid], [x + 1.4, mid]));
  return strokes;
}

/** The shed on the yard's north side, as a house: walls and a pitched roof. */
function shed(cx: number, baseZ: number, halfWidth: number, wallHeight: number): readonly MapPoint[] {
  return line(
    [cx - halfWidth, baseZ], [cx + halfWidth, baseZ], [cx + halfWidth, baseZ + wallHeight],
    [cx, baseZ + wallHeight * 1.8], [cx - halfWidth, baseZ + wallHeight], [cx - halfWidth, baseZ],
  );
}

/** The ground as he rules it. Fixed; nothing about the player changes it. */
export const HIS_MAP_RULED: readonly (readonly MapPoint[])[] = [
  // The shed her yard runs out of, inside the north fence.
  shed(-5, 5.2, 2.6, 2.4),
  ...water(WEST_X, EAST_X, DITCH_AT.channelNearZ, DITCH_AT.channelFarZ),
  // The field's sides and the yard fence at the top.
  line([HEDGEROW_BOUNDS.westX, DITCH_AT.nearBankZ], [HEDGEROW_BOUNDS.westX, NORTH_Z], [EAST_X, NORTH_Z], [EAST_X, DITCH_AT.nearBankZ]),
  // The field fence, with the one opening the boards were in.
  line([HEDGEROW_BOUNDS.westX, 0], [BOARDS_AT.x - BOARDS_HALF, 0]),
  line([BOARDS_AT.x + BOARDS_HALF, 0], [EAST_X, 0]),
  // The ditch's two banks.
  line([WEST_X, DITCH_AT.channelNearZ], [EAST_X, DITCH_AT.channelNearZ]),
  line([WEST_X, DITCH_AT.channelFarZ], [EAST_X, DITCH_AT.channelFarZ]),
  // The culvert, as a box, because to him it is an asset on a plan.
  box(CULVERT_AT.x, CULVERT_AT.z, 0.9, 0.6),
  // The far hedge, and its gap.
  hedge(WEST_X, HEDGE_GAP_AT.x - HEDGE_GAP_HALF, HEDGE_GAP_AT.z),
  hedge(HEDGE_GAP_AT.x + HEDGE_GAP_HALF, EAST_X, HEDGE_GAP_AT.z),
  // The page's own frame, with a margin column ruled down its left side for
  // the dates. He rules a border; she never has.
  box((PAGE_WEST + EAST_X + 1.5) / 2, (NORTH_Z + SOUTH_Z) / 2, (EAST_X + 1.5 - PAGE_WEST) / 2, (NORTH_Z - SOUTH_Z) / 2 + 1.5),
  line([MARGIN_X, NORTH_Z + 1.5], [MARGIN_X, SOUTH_Z - 1.5]),
];

/**
 * Down the margin: when he walked it. Digits only (§7). Day and month, four
 * nights across the autumn beats 7–10 span. Nothing else reads them.
 */
export const HIS_MAP_DATES: readonly string[] = ['0309', '1109', '1409', '1909'];

/**
 * The fallback when the ledger has no hedgerow: a cold `?beat=11`, or a save
 * from before beat 7. The plan requires the beat to be legible to a fixture
 * that has played nothing. It is the way beat 10 actually goes: in at the
 * boards, across to the huddle, down the culvert route.
 */
export const HIS_MAP_AUTHORED_ROUTE: readonly MapPoint[] = [
  { x: HEDGEROW_NIGHT_SPAWN.x, z: NORTH_Z - 1 },
  { x: BOARDS_AT.x, z: 0 },
  { x: HERD_HUDDLE_AT.x, z: HERD_HUDDLE_AT.z },
  ...CULVERT_ROUTE.slice(1).map((point) => ({ x: point.x, z: point.z })),
];

/** Cells further apart than this are two walks, not one, and the pen lifts. */
const LIFT_METRES = 3;
/** How far a traced line may cut a corner of her wash. Metres. */
const SIMPLIFY_METRES = 1.2;

/**
 * Her wash as he would trace it: the walked cells ordered into lines.
 *
 * The wash is a SET of cells with no order, so a line through it has to be
 * made. Nearest neighbour from the cell closest to where beat 10 comes in,
 * lifting the pen at a gap, then simplified: a line through every cell of a
 * wide walked area zigzags, and his line is neat. Sorted first, so the same
 * wash always makes the same line.
 */
export function tracedRoutes(
  wash: readonly MapPoint[],
  cellMetres: number,
  /** Where the pen starts. His map starts at the boards; her own trail (D-241) at her first cell. */
  start?: MapPoint,
): readonly (readonly MapPoint[])[] {
  const remaining = [...wash]
    .map((cell) => ({ x: (cell.x + 0.5) * cellMetres, z: (cell.z + 0.5) * cellMetres }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.z))
    .sort((a, b) => a.z - b.z || a.x - b.x);
  if (remaining.length === 0) return [];

  const lines: MapPoint[][] = [];
  let cursor: MapPoint = start ?? remaining[0] ?? { x: 0, z: 0 };
  let current: MapPoint[] = [];
  while (remaining.length > 0) {
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < remaining.length; i += 1) {
      const candidate = remaining[i];
      if (candidate === undefined) continue;
      const distance = Math.hypot(candidate.x - cursor.x, candidate.z - cursor.z);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    const [next] = remaining.splice(bestIndex, 1);
    if (next === undefined) break;
    if (current.length > 0 && bestDistance > LIFT_METRES) {
      lines.push(current);
      current = [];
    }
    current.push(next);
    cursor = next;
  }
  if (current.length > 0) lines.push(current);
  return lines.map((points) => simplify(points, SIMPLIFY_METRES)).filter((points) => points.length > 1);
}

/** Douglas–Peucker. His line keeps her corners and loses her dithering. */
function simplify(points: readonly MapPoint[], tolerance: number): readonly MapPoint[] {
  if (points.length < 3) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return points;
  const dx = last.x - first.x;
  const dz = last.z - first.z;
  const length = Math.hypot(dx, dz);
  let worst = 0;
  let worstIndex = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const point = points[i];
    if (point === undefined) continue;
    const distance = length === 0
      ? Math.hypot(point.x - first.x, point.z - first.z)
      : Math.abs(dz * point.x - dx * point.z + last.x * first.z - last.z * first.x) / length;
    if (distance > worst) {
      worst = distance;
      worstIndex = i;
    }
  }
  if (worst <= tolerance) return [first, last];
  const head = simplify(points.slice(0, worstIndex + 1), tolerance);
  const tail = simplify(points.slice(worstIndex), tolerance);
  return [...head.slice(0, -1), ...tail];
}

/** The whole sheet, built per raise from whatever the ledger holds now. */
export function hisMapSheet(wash: readonly MapPoint[], cellMetres: number): MentalMapSheet {
  const traced = tracedRoutes(wash, cellMetres, { x: BOARDS_AT.x, z: 0 });
  const routes = traced.length > 0 ? traced : [HIS_MAP_AUTHORED_ROUTE];
  const marks: Observation[] = routes.map((at, index) => ({
    id: `office.his-map.route-${String(index)}`,
    zone: HIS_MAP_ZONE,
    mark: { kind: 'route', at },
  }));
  // A pig at each end of her line, in his pencil: without it the line was a
  // dashed rising trend on a chart (D-240). With it, it is somebody's route.
  const first = routes[0]?.[0];
  const lastRoute = routes[routes.length - 1];
  const last = lastRoute?.[lastRoute.length - 1];
  const pictures = [first, last]
    .filter((at): at is MapPoint => at !== undefined)
    .map((at) => ({ glyph: 'pig' as const, at }));
  return { hand: 'his', wash: [], marks, cellMetres, ruled: HIS_MAP_RULED, dates: HIS_MAP_DATES, pictures };
}
